import api from '@/lib/api'
import { COMPLETE, ERROR, IN_PROGRESS } from '@/lib/constants/actionStatuses'
import { PATH_IMPORTS, PATH_IMPORT_TEMPLATE, PATH_IMPORT_FORMATS } from '@/lib/constants/api'
import buildInitialState from '@/store/helpers/buildInitialState'
import updateStatus from '@/store/helpers/updateStatus'
import { notifyError } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

const importUpload = {
  namespaced: true,
  state: buildInitialState({}),
  actions: {
    // The Custom Import Formats a Client may upload right now, so the screen can offer them beside the
    // standard upload. Empty for a Client with none, which is the common case. A read failure is not
    // fatal: it falls back to an empty list, leaving the standard upload, rather than blocking the page.
    fetchFormats ({ dispatch }, { clientId }) {
      return api.doGet({ path: PATH_IMPORT_FORMATS, parameters: { clientId } })
        .then(json => json.data || [])
        .catch(error => {
          notifyError(dispatch, error.message || 'Unable to load the import formats.')
          return []
        })
    },
    // Stage and validate one file for a Client. Nothing is written: the Run comes back Validated
    // (or Failed with a reason) and the admin decides whether to Import it. A bad file is not an
    // exception here -- the reason rides back on the run or as a plain error message.
    //
    // formatKey names a Custom Import Format the file is read as; omitted, the file is taken as the
    // Standard Import Format the pipeline reads directly.
    upload ({ commit, dispatch }, { clientId, file, formatKey }) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Uploading and validating, please wait...' })

      const fields = { ClientId: clientId }
      if (formatKey) {
        fields.FormatKey = formatKey
      }

      return api.doPostFile({ path: PATH_IMPORTS, file, fields })
        .then(response => {
          if (!response || !response.data) {
            throw new Error((response && response.errorMessage) || 'Unable to upload the file.')
          }
          commit('updateStatus', { status: COMPLETE })
          return response.data
        })
        .catch(error => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, error.message || 'Unable to upload the file.')
          throw error
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    // Pull the per-client Standard Import Format template for a date range and save it to disk with
    // the name the server chose. Identifiers arrive already filled in, so the admin never mistypes
    // an Assignment id or guesses at the column names.
    downloadTemplate ({ dispatch }, { clientId, startDate, endDate }) {
      showProgressModal({ dispatch, message: 'Generating the template, please wait...' })

      return api.doGetFile({
        path: PATH_IMPORT_TEMPLATE,
        parameters: { clientId, startDate, endDate },
        fallbackFileName: 'import-template.csv'
      })
        .catch(error => {
          notifyError(dispatch, error.message || 'Unable to generate the template.')
          throw error
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    }
  },
  mutations: {
    updateStatus
  }
}

export default importUpload
